import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { Alert, Button, DialogActions, DialogContent, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { AppModal } from '../../../../components/ui/AppModal';
import type { InstanceRow } from '../../instanceTypes';

interface DeleteInstanceModalProps {
  instance: InstanceRow | null;
  onClose: () => void;
  onDeleted: (id: string) => void;
  onDelete: (id: string) => Promise<void>;
}

export function DeleteInstanceModal({
  instance,
  onClose,
  onDeleted,
  onDelete,
}: DeleteInstanceModalProps) {
  const theme = useTheme();
  const { t } = useTranslation('instances');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const handleClose = () => {
    setError(null);
    onClose();
  };

  const handleConfirm = async () => {
    if (!instance) return;
    setError(null);
    setSubmitting(true);
    try {
      await onDelete(instance.id);
      onDeleted(instance.id);
      handleClose();
    } catch {
      setError(t('errors.deleteError'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AppModal
      open={instance !== null}
      onClose={handleClose}
      disableClose={submitting}
      maxWidth="xs"
      titleId="delete-inst-title"
      icon={<DeleteOutlineIcon sx={theme => ({ fontSize: theme.iconSize.sm })} />}
      title={t('delete.dialogTitle')}
      closeLabel={t('delete.cancel')}
    >
      <DialogContent sx={{ pt: 2 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <Typography>
          <Trans
            i18nKey="delete.dialogMessage"
            ns="instances"
            values={{ name: instance?.name ?? '' }}
            components={{ strong: <strong /> }}
          />
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Button
          variant="outlined"
          onClick={handleClose}
          disabled={submitting}
          sx={{ borderRadius: theme.borderRadius.pill, textTransform: 'none' }}
        >
          {t('delete.cancel')}
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={handleConfirm}
          disabled={submitting}
          sx={{ borderRadius: theme.borderRadius.pill, textTransform: 'none' }}
        >
          {t('delete.confirm')}
        </Button>
      </DialogActions>
    </AppModal>
  );
}
